import {useState} from 'react';
import * as Sentry from '@sentry/nextjs';

export type AsyncOperation<
	Arguments extends any[] = any[],
	Result = any,
	Error = any
> = {
	execute: (...args: Arguments) => void;
	data: Result | undefined;
	loading: boolean;
	error: Error;
	// completed with data
	fetched: boolean;
	// completed with no error
	completed: boolean;
};

export type AsyncOperationHooks<Arguments extends any[], Result> = {
	onExecute?: (...args: Arguments) => void;
	onSuccess?: (data: Result) => void;
	onError?: (error: any) => void;
};

/**
 * use a asynchronous promise as a declarative hook.
 * It will expose the data, loading and error state.
 * It is lazy because the execution of the operation must be triggered by the dev explicitly by calling execute
 */
export const useLazyPromise = <Arguments extends any[], Result, Error = any>(
	fnToPromise: (...args: Arguments) => Promise<Result>,
	hooks: AsyncOperationHooks<Arguments, Result> = {}
): AsyncOperation<Arguments, Result, Error> => {
	const [data, setData] = useState<Result | undefined>(undefined);
	const [error, setError] = useState<any>();
	const [loading, setLoading] = useState<boolean>(false);
	const [completed, setCompleted] = useState<boolean>(false);

	return {
		async execute(...args: Arguments) {
			try {
				setError(undefined);
				setLoading(true);
				hooks.onExecute?.(...args);
				const data = await fnToPromise(...args);
				setData(data);
				hooks.onSuccess?.(data);
				setCompleted(true);
			} catch (e) {
				// send this to sentry
				Sentry.captureException(e);
				// at first, then local management :
				setError(e);
				console.error(e);
				hooks.onError?.(e);
				console.log('🥲 Error managed');
			} finally {
				setLoading(false);
			}
		},
		fetched: completed && !loading && typeof data !== 'undefined',
		completed,
		data,
		loading,
		error
	};
};
